/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useState, useMemo, useEffect, useCallback} from 'react'
import ReactDOM from 'react-dom'
import PropTypes from 'prop-types'
import {useLocation} from 'react-router-dom'
import loadable from '@loadable/component'

// Chakra Components
import {
    AspectRatio,
    Box,
    Flex,
    Skeleton as ChakraSkeleton,
    // Hooks
    useBreakpointValue
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {findImageGroupBy} from '@salesforce/retail-react-app/app/utils/image-groups-utils'
import {Swiper, SwiperSlide} from 'swiper/react'
import DynamicImage from '@salesforce/retail-react-app/app/components/dynamic-image'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {getSiteEnvVarValue} from '../../utils/site-config'

const EnterKeyNumber = 13
const VIEW_TYPE_ZOOM = 'zoom'

// Lazy load ProductZoomModal - only loaded when user clicks to zoom an image
const ProductZoomModal = loadable(() => import('../product-zoom-modal'), {ssr: false})

/**
 * The skeleton representation of the image gallery component. Use this component while
 * you are waiting for product data to be returnd from the server.
 */
export const Skeleton = () => {
    const slideWidth = useBreakpointValue({base: '100px', md: '215px', lg: '315px'})
    const slideHeight = useBreakpointValue({base: '201px', md: '360px', lg: '460px'})
    const slideRatio = useBreakpointValue({base: 100 / 201, md: 215 / 360, lg: 315 / 460})
    const swiperPaddingX = useBreakpointValue({base: '16px', lg: '0'})

    return (
        <Box data-testid="sf-image-gallery-skeleton">
            <Swiper
                spaceBetween={4}
                slidesPerView={2.5}
                breakpointsBase="window"
                style={{
                    margin: 0,
                    paddingLeft: swiperPaddingX,
                    paddingRight: swiperPaddingX
                }}
                breakpoints={{
                    992: {
                        slidesPerView: 1.4
                    }
                }}
            >
                {new Array(4).fill(0).map((_, index) => (
                    <SwiperSlide
                        key={index}
                        style={{width: slideWidth, height: slideHeight, flexShrink: 0}}
                    >
                        <AspectRatio
                            ratio={slideRatio}
                            height="100%"
                        >
                            <ChakraSkeleton />
                        </AspectRatio>
                    </SwiperSlide>
                ))}
            </Swiper>
        </Box>
    )
}

/**
 * The image gallery displays a hero image and thumbnails below it. You can control which
 * image groups that are use by passing in the current selected variation values.
 */
const ImageList = ({imageGroups = [], selectedVariationAttributes = {}, lazy = false}) => {
    const [selectedIndex, setSelectedIndex] = useState(0)
    const [isModalOpen, setIsModalOpen] = useState(false)
    const location = useLocation()

    useEffect(() => {
        // reset the selected index when location search changes
        setSelectedIndex(0)
        setIsModalOpen(false)
    }, [location.search])

    // Get a memoized image group used for the thumbnails. We use the `useMemo` hook
    // so we don't have to waste time filtering the image groups each render if the
    // selected variation attributes haven't changed.
    const thumbnailImageGroup = useMemo(
        () =>
            findImageGroupBy(imageGroups, {
                viewType: 'large',
                selectedVariationAttributes
            }),
        [selectedVariationAttributes]
    )

    const zoomImageGroup = useMemo(
        () =>
            findImageGroupBy(imageGroups, {
                viewType: VIEW_TYPE_ZOOM,
                selectedVariationAttributes
            }),
        [selectedVariationAttributes]
    )

    const thumbnailImages = thumbnailImageGroup?.images || []
    const zoomImages = zoomImageGroup?.images || thumbnailImages
    const loadingStrategy = lazy ? 'lazy' : 'eager'

    const {app: appConfig} = getConfig()
    const {site} = useMultiSite()
    const PRODUCT_IMGS_RESIZE_PARAMS_ENABLED = getSiteEnvVarValue(appConfig, 'PRODUCT_IMGS_RESIZE_PARAMS_ENABLED', site?.id) == 'true'

    const getImageUrl = useCallback((image) => {
        const baseUrl = image.disBaseLink || image.link
        return PRODUCT_IMGS_RESIZE_PARAMS_ENABLED
            ? `${baseUrl}[?sw={width}&q=75&fmt=webp]`
            : baseUrl
    }, [PRODUCT_IMGS_RESIZE_PARAMS_ENABLED])

    const handleThumbnailSelect = useCallback((index) => {
        setSelectedIndex(index)
        if (zoomImages.length) {
            setIsModalOpen(true)
        }
    }, [zoomImages.length])

    const handleCloseModal = useCallback(() => {
        setIsModalOpen(false)
    }, [])
    const swiperPaddingX = useBreakpointValue({base: '16px', lg: '0'})

    return (
        <Flex direction="column">
            <Swiper
                spaceBetween={4}
                slidesPerView={2.5}
                breakpointsBase="window"
                style={{
                    margin: 0,
                    paddingLeft: swiperPaddingX,
                    paddingRight: swiperPaddingX
                }}
                breakpoints={{
                    992: {
                        slidesPerView: 1.4
                    }
                }}
            >
                {thumbnailImages.map((image, index) => {
                    const selected = index === selectedIndex
                    return (
                        <SwiperSlide
                            key={index}
                        >
                            <AspectRatio ratio={2 / 3} h={'100%'}>
                                <Box
                                    as="button"
                                    aria-pressed={selected ? 'true' : 'false'}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.keyCode === EnterKeyNumber) {
                                            e.preventDefault()
                                            return handleThumbnailSelect(index)
                                        }
                                    }}
                                    onClick={() => handleThumbnailSelect(index)}
                                    data-testid="image-gallery-thumbnails"
                                >
                                    <DynamicImage
                                        src={PRODUCT_IMGS_RESIZE_PARAMS_ENABLED
                                            ? `${image.disBaseLink || image.link}[?sw={width}]`
                                            : image.disBaseLink || image.link}
                                        widths={{
                                            base: '400px'
                                        }}
                                        imageProps={{
                                            alt: image.alt,
                                            loading: loadingStrategy,
                                            width: '100%'
                                        }}
                                        width='100%'
                                        height='100%'
                                    />
                                </Box>
                            </AspectRatio>
                        </SwiperSlide>
                    )
                })}
            </Swiper>

            {typeof document !== 'undefined' &&
                ReactDOM.createPortal(
                    <ProductZoomModal
                        isOpen={isModalOpen}
                        onClose={handleCloseModal}
                        images={zoomImages}
                        thumbImages={thumbnailImages}
                        initialImageIndex={selectedIndex}
                        getImageUrl={getImageUrl}
                    />,
                    document.body
                )}
        </Flex>
    )
}

ImageList.propTypes = {
    /**
     * The images array to be rendered
     */
    imageGroups: PropTypes.array,
    /**
     * The current selected variation values
     */
    selectedVariationAttributes: PropTypes.object,
    /**
     * Determines whether the image will be lazy loaded or not
     */
    lazy: PropTypes.bool
}

export default ImageList
